import { useCallback, useRef } from 'react';
import { Shape, Tool } from '../types/Shape';
import { findNearestConnectionPoint, findShapeAtPoint, getRelativePosition, getConnectionPoints, getAbsolutePosition } from '../utils/connectionUtils';

export interface UseCanvasMouseMoveProps {
  // Current state
  currentTool: Tool;
  shapes: Shape[];
  stagePos: { x: number; y: number };
  stageScale: number;
  isDrawing: boolean;
  currentShape: Partial<Shape> | null;
  isResizing: boolean;
  resizeHandle: string | null;
  resizeStartPos: { x: number; y: number } | null;
  resizeStartShape: { x: number; y: number; width: number; height: number } | null;
  selectedShapes: string[];
  isCanvasDragging: boolean;
  isWheelPanning: boolean;
  canvasDragStart: { x: number; y: number } | null;
  isConnecting: boolean;
  isDraggingArrowEndpoint: boolean;
  isLassoSelecting: boolean;
  lassoStart: { x: number; y: number } | null;
  editingShape: string | null;
  editingMarkdownPosition: { x: number; y: number; width: number; height: number } | null;
  editingCodePosition: { x: number; y: number; width: number; height: number } | null;
  canvasContainerRef: React.RefObject<HTMLDivElement>;

  // Refs
  canvasDragRafRef: React.MutableRefObject<number | null>;

  // Setters
  setMousePos: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setNearbyShapeIds: React.Dispatch<React.SetStateAction<string[]>>;
  setLassoEnd: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setStagePos: React.Dispatch<React.SetStateAction<{ x: number; y: number }>>;
  setCanvasDragStart: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setIsCanvasDragging: React.Dispatch<React.SetStateAction<boolean>>;
  setSnappedPoint: React.Dispatch<React.SetStateAction<{ x: number; y: number } | null>>;
  setCurrentShape: React.Dispatch<React.SetStateAction<Partial<Shape> | null>>;
  setShapes: React.Dispatch<React.SetStateAction<Shape[]>>;
  setEditingMarkdownPosition: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;
  setEditingCodePosition: React.Dispatch<React.SetStateAction<{ x: number; y: number; width: number; height: number } | null>>;
}

export interface UseCanvasMouseMoveReturn {
  handleStageMouseMove: (e: any) => void;
}

export function useCanvasMouseMove({
  currentTool,
  shapes,
  stagePos,
  stageScale,
  isDrawing,
  currentShape,
  isResizing,
  resizeHandle,
  resizeStartPos,
  resizeStartShape,
  selectedShapes,
  isCanvasDragging,
  isWheelPanning,
  canvasDragStart,
  isConnecting,
  isDraggingArrowEndpoint,
  isLassoSelecting,
  lassoStart,
  editingShape,
  editingMarkdownPosition,
  editingCodePosition,
  canvasContainerRef,
  canvasDragRafRef,
  setMousePos,
  setNearbyShapeIds,
  setLassoEnd,
  setStagePos,
  setCanvasDragStart,
  setIsCanvasDragging,
  setSnappedPoint,
  setCurrentShape,
  setShapes,
  setEditingMarkdownPosition,
  setEditingCodePosition,
}: UseCanvasMouseMoveProps): UseCanvasMouseMoveReturn {
  const rafRef = useRef<number>(0);
  const innerRef = useRef<(e: any) => void>(() => {});

  const handleStageMouseMove = useCallback((e: any) => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const event = e;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = 0;
      innerRef.current(event);
    });
  }, []);

  const handleMouseMoveInner = useCallback((e: any) => {
    // Update mouse position for arrow tool (nearby shape detection)
    if (currentTool === 'arrow' && !isConnecting && !isDraggingArrowEndpoint) {
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const actualX = (pointer.x - stagePos.x) / stageScale;
          const actualY = (pointer.y - stagePos.y) / stageScale;
          setMousePos({ x: actualX, y: actualY });

          // Find nearby shapes (100px radius)
          const proximityThreshold = 100 / stageScale;
          const nearby = shapes
            .filter(s => s.renderMode === 'html' && ['rect', 'circle'].includes(s.type))
            .filter(s => {
              const shapeX = s.x + (s.width || s.radius || 50) / 2;
              const shapeY = s.y + (s.height || s.radius || 50) / 2;
              const distance = Math.sqrt(
                Math.pow(actualX - shapeX, 2) + Math.pow(actualY - shapeY, 2)
              );
              return distance < proximityThreshold;
            })
            .map(s => s.id);

          setNearbyShapeIds(nearby);
        }
      }
    } else {
      setNearbyShapeIds([]);
    }

    // Lasso selection - update end point
    if (isLassoSelecting && lassoStart) {
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const scale = stage.scaleX();
          const currentStagePos = { x: stage.x(), y: stage.y() };
          const actualX = (pointer.x - currentStagePos.x) / scale;
          const actualY = (pointer.y - currentStagePos.y) / scale;
          setLassoEnd({ x: actualX, y: actualY });
        }
      }
      return;
    }

    // Canvas drag with wheel button
    if (isCanvasDragging && isWheelPanning && canvasDragStart) {
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const deltaX = pointer.x - canvasDragStart.x;
          const deltaY = pointer.y - canvasDragStart.y;

          if (canvasDragRafRef.current !== null) {
            cancelAnimationFrame(canvasDragRafRef.current);
          }

          canvasDragRafRef.current = requestAnimationFrame(() => {
            setStagePos(prev => ({
              x: prev.x + deltaX,
              y: prev.y + deltaY
            }));
            setCanvasDragStart({ x: pointer.x, y: pointer.y });
          });
        }
      }
      return;
    }

    // Reset canvas drag if wheel panning is false
    if (isCanvasDragging && !isWheelPanning) {
      setIsCanvasDragging(false);
      setCanvasDragStart(null);
    }

    // Arrow drawing - update endpoint preview
    if (isConnecting && currentShape && currentShape.type === 'arrow') {
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const actualX = (pointer.x - stagePos.x) / stageScale;
          const actualY = (pointer.y - stagePos.y) / stageScale;

          const nearestPoint = findNearestConnectionPoint(actualX, actualY, shapes, 20 / stageScale);

          let finalX = actualX;
          let finalY = actualY;

          if (nearestPoint) {
            finalX = nearestPoint.x;
            finalY = nearestPoint.y;
            setSnappedPoint(nearestPoint);
          } else {
            setSnappedPoint(null);
          }

          setCurrentShape(prev => prev ? {
            ...prev,
            width: finalX - (prev.x || 0),
            height: finalY - (prev.y || 0)
          } : null);
        }
      }
      return;
    }

    // Resize handling
    if (isResizing && resizeHandle && resizeStartPos && resizeStartShape) {
      const stage = e.target.getStage();
      if (stage) {
        const pointer = stage.getPointerPosition();
        if (pointer) {
          const shape = shapes.find(s => s.id === selectedShapes[0]);
          if (shape) {
            const scale = stage.scaleX();
            const deltaX = (pointer.x - resizeStartPos.x) / scale;
            const deltaY = (pointer.y - resizeStartPos.y) / scale;

            let newX = resizeStartShape.x;
            let newY = resizeStartShape.y;
            let newWidth = resizeStartShape.width;
            let newHeight = resizeStartShape.height;

            // Arrow special handling
            if (shape.type === 'arrow') {
              switch (resizeHandle) {
                case 'nw':
                  const endX = resizeStartShape.x + resizeStartShape.width;
                  const endY = resizeStartShape.y + resizeStartShape.height;
                  newX = resizeStartShape.x + deltaX;
                  newY = resizeStartShape.y + deltaY;

                  const nearestStartPoint = findNearestConnectionPoint(newX, newY, shapes.filter(s => s.type !== 'arrow'), 30);
                  if (nearestStartPoint) {
                    setSnappedPoint(nearestStartPoint);
                    newX = nearestStartPoint.x;
                    newY = nearestStartPoint.y;
                  } else {
                    setSnappedPoint(null);
                  }

                  newWidth = endX - newX;
                  newHeight = endY - newY;
                  break;

                case 'se':
                  const newEndX = resizeStartShape.x + resizeStartShape.width + deltaX;
                  const newEndY = resizeStartShape.y + resizeStartShape.height + deltaY;

                  const nearestEndPoint = findNearestConnectionPoint(newEndX, newEndY, shapes.filter(s => s.type !== 'arrow'), 30);
                  if (nearestEndPoint) {
                    setSnappedPoint(nearestEndPoint);
                    newWidth = nearestEndPoint.x - resizeStartShape.x;
                    newHeight = nearestEndPoint.y - resizeStartShape.y;
                  } else {
                    setSnappedPoint(null);
                    newWidth = resizeStartShape.width + deltaX;
                    newHeight = resizeStartShape.height + deltaY;
                  }
                  break;
              }
            } else {
              // Rectangle/Circle resize logic
              switch (resizeHandle) {
                case 'nw':
                  newX = resizeStartShape.x + deltaX;
                  newY = resizeStartShape.y + deltaY;
                  newWidth = resizeStartShape.width - deltaX;
                  newHeight = resizeStartShape.height - deltaY;
                  break;
                case 'ne':
                  newY = resizeStartShape.y + deltaY;
                  newWidth = resizeStartShape.width + deltaX;
                  newHeight = resizeStartShape.height - deltaY;
                  break;
                case 'sw':
                  newX = resizeStartShape.x + deltaX;
                  newWidth = resizeStartShape.width - deltaX;
                  newHeight = resizeStartShape.height + deltaY;
                  break;
                case 'se':
                  newWidth = resizeStartShape.width + deltaX;
                  newHeight = resizeStartShape.height + deltaY;
                  break;
                case 'n':
                  newY = resizeStartShape.y + deltaY;
                  newHeight = resizeStartShape.height - deltaY;
                  break;
                case 's':
                  newHeight = resizeStartShape.height + deltaY;
                  break;
                case 'w':
                  newX = resizeStartShape.x + deltaX;
                  newWidth = resizeStartShape.width - deltaX;
                  break;
                case 'e':
                  newWidth = resizeStartShape.width + deltaX;
                  break;
              }

              // Minimum size for rect/circle
              newWidth = Math.max(newWidth, 20);
              newHeight = Math.max(newHeight, 20);

              // Ctrl key for square/circle constraint
              if (e.evt.ctrlKey) {
                const maxDimension = Math.max(Math.abs(newWidth), Math.abs(newHeight));
                const signWidth = newWidth >= 0 ? 1 : -1;
                const signHeight = newHeight >= 0 ? 1 : -1;
                newWidth = maxDimension * signWidth;
                newHeight = maxDimension * signHeight;

                switch (resizeHandle) {
                  case 'nw':
                    newX = resizeStartShape.x + resizeStartShape.width - newWidth;
                    newY = resizeStartShape.y + resizeStartShape.height - newHeight;
                    break;
                  case 'ne':
                    newY = resizeStartShape.y + resizeStartShape.height - newHeight;
                    break;
                  case 'sw':
                    newX = resizeStartShape.x + resizeStartShape.width - newWidth;
                    break;
                }
              }
            }

            // Update shapes
            setShapes(prev => {
              const updatedShapes = prev.map(s => {
                if (s.id === shape.id) {
                  if (shape.type === 'arrow') {
                    const updatedArrow = { ...s, x: newX, y: newY, width: newWidth, height: newHeight };
                    const startX = newX;
                    const startY = newY;
                    const endXPos = newX + newWidth;
                    const endYPos = newY + newHeight;

                    // Start point connection
                    const shapeAtStart = findShapeAtPoint(startX, startY, prev.filter(sh => sh.type !== 'arrow'));
                    if (shapeAtStart) {
                      const relativePos = getRelativePosition(startX, startY, shapeAtStart);
                      if (relativePos) {
                        updatedArrow.startShapeId = shapeAtStart.id;
                        updatedArrow.startConnectionPoint = undefined;
                        updatedArrow.startRelativeX = relativePos.relativeX;
                        updatedArrow.startRelativeY = relativePos.relativeY;
                      }
                    } else if (updatedArrow.startShapeId) {
                      updatedArrow.startShapeId = undefined;
                      updatedArrow.startConnectionPoint = undefined;
                      updatedArrow.startRelativeX = undefined;
                      updatedArrow.startRelativeY = undefined;
                    }

                    // End point connection
                    const shapeAtEnd = findShapeAtPoint(endXPos, endYPos, prev.filter(sh => sh.type !== 'arrow'));
                    if (shapeAtEnd) {
                      const relativePos = getRelativePosition(endXPos, endYPos, shapeAtEnd);
                      if (relativePos) {
                        updatedArrow.endShapeId = shapeAtEnd.id;
                        updatedArrow.endConnectionPoint = undefined;
                        updatedArrow.endRelativeX = relativePos.relativeX;
                        updatedArrow.endRelativeY = relativePos.relativeY;
                      }
                    } else if (updatedArrow.endShapeId) {
                      updatedArrow.endShapeId = undefined;
                      updatedArrow.endConnectionPoint = undefined;
                      updatedArrow.endRelativeX = undefined;
                      updatedArrow.endRelativeY = undefined;
                    }

                    return updatedArrow;
                  }
                  return { ...s, x: newX, y: newY, width: newWidth, height: newHeight };
                } else if (selectedShapes.includes(s.id)) {
                  const originalShape = shapes.find(orig => orig.id === s.id);
                  if (originalShape) {
                    const deltaX_move = newX - resizeStartShape.x;
                    const deltaY_move = newY - resizeStartShape.y;
                    return { ...s, x: originalShape.x + deltaX_move, y: originalShape.y + deltaY_move };
                  }
                }
                return s;
              });

              // Update connected arrows for non-arrow shapes
              if (shape.type !== 'arrow') {
                selectedShapes.forEach(shapeId => {
                  const updatedShape = updatedShapes.find(s => s.id === shapeId);
                  if (updatedShape && updatedShape.connectedArrows) {
                    updatedShape.connectedArrows.forEach(arrowId => {
                      const arrow = updatedShapes.find(s => s.id === arrowId);
                      if (arrow && arrow.type === 'arrow') {
                        let arrowEndX = arrow.x + (arrow.width || 0);
                        let arrowEndY = arrow.y + (arrow.height || 0);

                        if (arrow.startShapeId === shapeId) {
                          if (arrow.startConnectionPoint) {
                            const connectionPoints = getConnectionPoints(updatedShape);
                            const startPoint = connectionPoints[arrow.startConnectionPoint];
                            if (startPoint) {
                              arrow.x = startPoint.x;
                              arrow.y = startPoint.y;
                              arrow.width = arrowEndX - startPoint.x;
                              arrow.height = arrowEndY - startPoint.y;
                            }
                          } else if (arrow.startRelativeX !== undefined && arrow.startRelativeY !== undefined) {
                            const absolutePos = getAbsolutePosition(arrow.startRelativeX, arrow.startRelativeY, updatedShape);
                            arrow.x = absolutePos.x;
                            arrow.y = absolutePos.y;
                            arrow.width = arrowEndX - absolutePos.x;
                            arrow.height = arrowEndY - absolutePos.y;
                          }
                        }
                        if (arrow.endShapeId === shapeId) {
                          if (arrow.endConnectionPoint) {
                            const connectionPoints = getConnectionPoints(updatedShape);
                            const endPoint = connectionPoints[arrow.endConnectionPoint];
                            if (endPoint) {
                              arrow.width = endPoint.x - arrow.x;
                              arrow.height = endPoint.y - arrow.y;
                            }
                          } else if (arrow.endRelativeX !== undefined && arrow.endRelativeY !== undefined) {
                            const absolutePos = getAbsolutePosition(arrow.endRelativeX, arrow.endRelativeY, updatedShape);
                            arrow.width = absolutePos.x - arrow.x;
                            arrow.height = absolutePos.y - arrow.y;
                          }
                        }
                      }
                    });
                  }
                });
              }

              return updatedShapes;
            });

            // Update inline editor position during resize
            if (editingShape === shape.id) {
              const stageEl = e.target.getStage();
              if (stageEl && canvasContainerRef.current) {
                const stageBox = stageEl.container().getBoundingClientRect();
                const group = e.target.getParent();
                if (group) {
                  const transform = group.getAbsoluteTransform();
                  const screenPos = transform.point({ x: 0, y: 0 });
                  const currentScale = stageEl.scaleX();

                  const newPos = {
                    x: stageBox.left + screenPos.x,
                    y: stageBox.top + screenPos.y,
                    width: newWidth * currentScale,
                    height: newHeight * currentScale
                  };

                  if (shape.type === 'md' && editingMarkdownPosition) {
                    setEditingMarkdownPosition(newPos);
                  } else if (shape.type === 'code' && editingCodePosition) {
                    setEditingCodePosition(newPos);
                  }
                }
              }
            }
          }
        }
      }
      return;
    }

    // Shape drawing
    if (!isDrawing || !currentShape) return;

    const stage = e.target.getStage();
    if (!stage) return;

    const pos = stage.getPointerPosition();
    if (!pos) return;

    const scale = stage.scaleX();
    const currentStagePos = { x: stage.x(), y: stage.y() };
    const actualX = (pos.x - currentStagePos.x) / scale;
    const actualY = (pos.y - currentStagePos.y) / scale;

    let newWidth = actualX - currentShape.x!;
    let newHeight = actualY - currentShape.y!;

    // Ctrl key for square/circle constraint
    if (e.evt.ctrlKey && (currentShape.type === 'rect' || currentShape.type === 'circle')) {
      const maxDimension = Math.max(Math.abs(newWidth), Math.abs(newHeight));
      newWidth = maxDimension * (newWidth >= 0 ? 1 : -1);
      newHeight = maxDimension * (newHeight >= 0 ? 1 : -1);
    }

    if (Math.abs(newWidth - (currentShape.width || 0)) > 0.1 ||
      Math.abs(newHeight - (currentShape.height || 0)) > 0.1) {
      setCurrentShape(prev => ({
        ...prev,
        width: newWidth,
        height: newHeight,
      }));
    }
  }, [
    currentTool,
    shapes,
    stagePos,
    stageScale,
    isDrawing,
    currentShape,
    isResizing,
    resizeHandle,
    resizeStartPos,
    resizeStartShape,
    selectedShapes,
    isCanvasDragging,
    isWheelPanning,
    canvasDragStart,
    isConnecting,
    isDraggingArrowEndpoint,
    isLassoSelecting,
    lassoStart,
    editingShape,
    editingMarkdownPosition,
    editingCodePosition,
    canvasContainerRef,
    canvasDragRafRef,
    setMousePos,
    setNearbyShapeIds,
    setLassoEnd,
    setStagePos,
    setCanvasDragStart,
    setIsCanvasDragging,
    setSnappedPoint,
    setCurrentShape,
    setShapes,
    setEditingMarkdownPosition,
    setEditingCodePosition,
  ]);

  innerRef.current = handleMouseMoveInner;

  return { handleStageMouseMove };
}
